/**
 * Ernährung: Makros rechnen, nicht schätzen.
 *
 * Die Zahlen selbst (2800 kcal, 160 g Protein …) stehen in `docs/fachkonzept.md`
 * mit Quelle. Hier steht nur die Arithmetik.
 */

/** Energiedichte in kcal je Gramm. Atwater-Faktoren, gerundet wie üblich. */
export const KCAL_PER_GRAM = { protein: 4, carbs: 4, fat: 9 } as const;

export interface Macros {
  kcal: number;
  protein: number;
  carbs: number;
  fat: number;
}

export const ZERO_MACROS: Macros = { kcal: 0, protein: 0, carbs: 0, fat: 0 };

/** Nährwerte je 100 g — so stehen sie auf jeder Packung. */
export interface NutritionPer100g {
  kcal: number;
  protein: number;
  carbs: number;
  fat: number;
}

/** Nährwerte einer tatsächlich gegessenen Menge. */
export function macrosForAmount(per100g: NutritionPer100g, grams: number): Macros {
  const factor = grams / 100;
  return {
    kcal: per100g.kcal * factor,
    protein: per100g.protein * factor,
    carbs: per100g.carbs * factor,
    fat: per100g.fat * factor,
  };
}

export function sumMacros(entries: readonly Macros[]): Macros {
  return entries.reduce<Macros>(
    (total, entry) => ({
      kcal: total.kcal + entry.kcal,
      protein: total.protein + entry.protein,
      carbs: total.carbs + entry.carbs,
      fat: total.fat + entry.fat,
    }),
    { ...ZERO_MACROS },
  );
}

export function roundMacros(macros: Macros): Macros {
  return {
    kcal: Math.round(macros.kcal),
    protein: Math.round(macros.protein),
    carbs: Math.round(macros.carbs),
    fat: Math.round(macros.fat),
  };
}

/**
 * Aus Makros berechnete Energie. Weicht regelmäßig ein paar Prozent vom
 * aufgedruckten Wert ab (Ballaststoffe, Alkohol, Rundung des Herstellers) — das ist
 * normal und wird **nicht** stillschweigend korrigiert. Nützlich, um grob falsche
 * Eingaben zu erkennen.
 */
export function kcalFromMacros(macros: Pick<Macros, 'protein' | 'carbs' | 'fat'>): number {
  return (
    macros.protein * KCAL_PER_GRAM.protein +
    macros.carbs * KCAL_PER_GRAM.carbs +
    macros.fat * KCAL_PER_GRAM.fat
  );
}

export interface MacroTargets {
  kcal: number;
  protein: number;
  carbs: number;
  fat: number;
}

export interface MacroProgress {
  consumed: Macros;
  targets: MacroTargets;
  /** Anteil je Nährwert, 0–1+ (über 1 heißt: Ziel überschritten). */
  ratio: Macros;
  remaining: Macros;
}

export function macroProgress(consumed: Macros, targets: MacroTargets): MacroProgress {
  const share = (value: number, target: number): number => (target > 0 ? value / target : 0);
  return {
    consumed,
    targets,
    ratio: {
      kcal: share(consumed.kcal, targets.kcal),
      protein: share(consumed.protein, targets.protein),
      carbs: share(consumed.carbs, targets.carbs),
      fat: share(consumed.fat, targets.fat),
    },
    remaining: {
      kcal: targets.kcal - consumed.kcal,
      protein: targets.protein - consumed.protein,
      carbs: targets.carbs - consumed.carbs,
      fat: targets.fat - consumed.fat,
    },
  };
}

// --- Mahlzeiten --------------------------------------------------------------

/**
 * Die Slots eines Tages.
 *
 * Aus dem Fachkonzept §2: vier bis fünf Portionen à 30–40 g Protein, alle 3–5
 * Stunden. `pre` und `post` stehen getrennt, weil sie andere Anforderungen haben
 * (kohlenhydratbetont und fettarm davor, Protein danach) — nicht, weil das
 * Timing entscheidend wäre. Entscheidend ist die Tagesmenge.
 */
export const MEAL_SLOTS = ['breakfast', 'lunch', 'pre', 'post', 'dinner', 'snack'] as const;

export type MealSlot = (typeof MEAL_SLOTS)[number];

/** Weitere Snacks, die man im Tag hinzufügen kann — höchstens drei (16.10.2026). */
export const EXTRA_SNACKS = ['snack2', 'snack3', 'snack4'] as const;

/**
 * Alle Plätze eines Tages: die sechs festen und die weiteren Snacks. Rezepte
 * behalten ihre Kategorie aus `MEAL_SLOTS`; Protokoll und Plan nutzen diese.
 */
export const DAY_SLOTS = [...MEAL_SLOTS, ...EXTRA_SNACKS] as const;

export type DaySlot = (typeof DAY_SLOTS)[number];

export function scaleMacros(macros: Macros, factor: number): Macros {
  return {
    kcal: macros.kcal * factor,
    protein: macros.protein * factor,
    carbs: macros.carbs * factor,
    fat: macros.fat * factor,
  };
}

/**
 * Woher die Nährwerte eines Rezepts kommen.
 *
 * `ingredients` — aus den Zutaten gerechnet; ändert sich eine Zutat, ändert sich
 * das Rezept. `entered` — direkt eingetragen, etwa aus einer Schätzung oder von
 * der Verpackung eines Fertiggerichts.
 *
 * Beides muss es geben: Wer jedes Standardgericht erst in Zutaten zerlegen
 * müsste, trägt es nie ein — und ein Gericht ohne Nährwerte ist im Protokoll
 * wertlos. Die App zeigt die Herkunft an, statt beides gleich aussehen zu lassen.
 */
export type MacroSource = 'ingredients' | 'entered';

export interface RecipeIngredientInput {
  per100g: NutritionPer100g;
  grams: number;
}

/**
 * Nährwerte **einer Portion** aus den Zutaten.
 *
 * `null`, wenn keine Zutaten da sind oder die Portionszahl unsinnig ist — nicht
 * 0. Null Kalorien wäre eine Aussage; „unbekannt" ist die richtige.
 */
export function macrosPerPortion(
  ingredients: readonly RecipeIngredientInput[],
  portions: number,
): Macros | null {
  if (ingredients.length === 0 || portions <= 0) return null;
  const total = sumMacros(ingredients.map((entry) => macrosForAmount(entry.per100g, entry.grams)));
  return scaleMacros(total, 1 / portions);
}

export interface DayEntry {
  macros: Macros;
  slot: DaySlot;
}

export interface DaySummary {
  progress: MacroProgress;
  bySlot: Record<DaySlot, Macros>;
  /**
   * Wie viele Einträge mindestens `proteinPortionMinG` Protein hatten.
   *
   * Das Fachkonzept nennt vier bis fünf Portionen à 30–40 g als den größten
   * Hebel — größer als jedes Timing. Deshalb wird es gezählt und nicht nur die
   * Tagessumme gezeigt.
   */
  proteinPortions: number;
  entries: number;
}

export const PROTEIN_PORTION_MIN_G = 30;

export function summarizeDay(
  entries: readonly DayEntry[],
  targets: MacroTargets,
  proteinPortionMinG = PROTEIN_PORTION_MIN_G,
): DaySummary {
  const bySlot = Object.fromEntries(
    DAY_SLOTS.map((slot) => [
      slot,
      sumMacros(entries.filter((entry) => entry.slot === slot).map((entry) => entry.macros)),
    ]),
  ) as Record<DaySlot, Macros>;

  return {
    progress: macroProgress(sumMacros(entries.map((entry) => entry.macros)), targets),
    bySlot,
    proteinPortions: entries.filter((entry) => entry.macros.protein >= proteinPortionMinG).length,
    entries: entries.length,
  };
}

/**
 * Weicht die eingetragene Energie stark von der aus den Makros gerechneten ab?
 *
 * Kleine Abweichungen sind normal (Ballaststoffe, Alkohol, Herstellerrundung) und
 * werden **nicht** stillschweigend korrigiert. Grobe deuten auf einen Tippfehler
 * — eine verrutschte Null im Protein fällt sonst erst auf, wenn die Wochenbilanz
 * nicht mehr stimmt.
 */
export function kcalLooksWrong(entered: Macros, tolerance = 0.25): boolean {
  const computed = kcalFromMacros(entered);
  if (computed <= 0 || entered.kcal <= 0) return false;
  return Math.abs(entered.kcal - computed) / computed > tolerance;
}
